import { useCallback, useReducer } from "react";
import produce from "immer";
import _ from "lodash";

type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export type DefinitionState = {
	hasMore: boolean;
	limit: number;
	offset: number;
	name?: string;
	results: [];
	loading: boolean;
	totalCount: number;
};

// type ResetState = Partial<DefinitionState>;

// TODO: Feels like something is wrong here if you're omitting 5 of the 6 properties of the type
export type PageData = PartialBy<
	Omit<DefinitionState, "loading" | "error">,
	"offset" | "limit" | "name" | "totalCount"
>;

// Actions
const SET_DATA = "SET_DATA";
const APPEND_DATA = "APPEND_DATA";
const STATUS_LOADING = "STATUS_LOADING";
const STATUS_READY = "STATUS_READY";

interface SetDataAction {
	type: typeof SET_DATA;
	data: PageData;
}
interface AppendDataAction {
	type: typeof APPEND_DATA;
	data: PageData;
}
interface SetLoadingAction {
	type: typeof STATUS_LOADING;
}
interface SetReadyAction {
	type: typeof STATUS_READY;
}

type Actions =
	| SetDataAction
	| AppendDataAction
	| SetLoadingAction
	| SetReadyAction;

export const initialDefinitionState: DefinitionState = {
	hasMore: false,
	limit: 10,
	offset: 0,
	name: "",
	results: [],
	loading: true,
	totalCount: 0,
};

export const reducer = (
	state: DefinitionState,
	action: Actions
): DefinitionState => {
	return produce(state, draft => {
		switch (action.type) {
			case SET_DATA: {
				const { results, ...rest } = action.data;
				_.merge(draft, rest);
				draft.results = results;
				break;
			}

			case APPEND_DATA:
				const { results, ...rest } = action.data;
				draft.results = [...draft.results, ...results];
				_.assignWith(draft, rest);
				break;

			case STATUS_LOADING:
				draft.loading = true;
				break;

			case STATUS_READY:
				draft.loading = false;
				break;
		}
	});
};

export default (init: DefinitionState = initialDefinitionState) => {
	const [state, dispatch] = useReducer(reducer, init);

	const setData = useCallback(async (data: PageData) => {
		dispatch({ type: SET_DATA, data });
	}, []);

	const addPage = useCallback(async (data: PageData) => {
		dispatch({ type: APPEND_DATA, data });
	}, []);

	const loading = useCallback(() => {
		dispatch({ type: STATUS_LOADING });
	}, []);

	const ready = useCallback(() => {
		dispatch({ type: STATUS_READY });
	}, []);

	return {
		state,
		setData,
		addPage,
		loading,
		ready,
	};
};
